import { useMemo } from "react";
import useCapabilities, { useHasRoles } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { isStaffMcpPlaygroundRuntime } from "@/features/mcp-playground/access";
import type { InteractionTopicDepartment } from "./interaction-topics/queries";
import { INTERACTION_TOPIC_DEPARTMENTS } from "./interaction-topics/queries";

export function canAccessM360Configuration(hasGlobalAdminRole: boolean): boolean {
  return isStaffMcpPlaygroundRuntime() && hasGlobalAdminRole;
}

export function useConfigurationAccess() {
  const { loaded } = useCapabilities();
  const isGlobalAdmin = useHasRoles([ROLE.GLOBAL_ADMIN]);
  const canView = canAccessM360Configuration(isGlobalAdmin);

  return { loaded, canView };
}

// Department admin roles mapped to the topic lists they may read and edit.
// Mirrors InteractionTopicConfig::ADMIN_DEPARTMENTS on the backend. Global
// admins may access every department (and are the only ones who may access
// Player Engagement, which has no admin role).
const TOPIC_DEPARTMENTS_BY_ADMIN_ROLE: Record<string, InteractionTopicDepartment[]> = {
  [ROLE.CAREER_SERVICES_ADMIN]: ["career_services", "recruiting"],
  [ROLE.MEMBERSHIP_SERVICES_ADMIN]: ["membership_services"],
  [ROLE.SECTION_ADMIN]: ["section"],
  [ROLE.EDUCATION_ADMIN]: ["education"]
};

const TOPICS_VIEW_ROLES: string[] = [ROLE.GLOBAL_ADMIN, ...Object.keys(TOPIC_DEPARTMENTS_BY_ADMIN_ROLE)];

export function useCanViewInteractionTopicsTab(): boolean {
  const hasAnyRole = useHasRoles(TOPICS_VIEW_ROLES);
  return isStaffMcpPlaygroundRuntime() && hasAnyRole;
}

const ALL_TOPIC_DEPARTMENTS: InteractionTopicDepartment[] = INTERACTION_TOPIC_DEPARTMENTS.map((dept) => dept.value);

export function useInteractionTopicsAccess() {
  const { loaded, hasRole } = useCapabilities();
  const departments = useMemo<InteractionTopicDepartment[]>(() => {
    if (hasRole(ROLE.GLOBAL_ADMIN)) return [...ALL_TOPIC_DEPARTMENTS];
    const allowed = new Set<InteractionTopicDepartment>();
    for (const [role, depts] of Object.entries(TOPIC_DEPARTMENTS_BY_ADMIN_ROLE)) {
      if (hasRole(role as ROLE)) depts.forEach((dept) => allowed.add(dept));
    }
    return ALL_TOPIC_DEPARTMENTS.filter((dept) => allowed.has(dept));
  }, [hasRole]);
  const canView = isStaffMcpPlaygroundRuntime() && (hasRole(ROLE.GLOBAL_ADMIN) || departments.length > 0);

  return { loaded, canView, departments };
}
